import { useRouter } from 'next/navigation'
import { datadogLogs } from '@datadog/browser-logs'
import TimesIcon from 'src/app/assets/icons/ico-times.svg'
import ArrowLeftIcon from 'src/app/assets/icons/ico-arrow-left.svg'
import One55SymbolWhiteSimple from 'src/app/assets/icons/ico-155-symbol-simple.svg'
import { Button } from 'src/components/Button'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage, useGetGameConfig } from 'src/store/game'
import { useUIActions } from 'src/store/ui'
import { useSoundActions } from 'src/store/sound'
import { useGetCurrentPlayerId } from 'src/store/player'
import { lobbyPath } from 'src/utils/lobbyPath'
import ExternalLinkIcon from 'src/app/assets/icons/ico-external-link.svg'

type Props = Readonly<{
  isScheduled: boolean
  gameId: string | null
}>

export function TracksPanelButtons({ isScheduled, gameId }: Props) {
  const router = useRouter()
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const { tracksPanelOpened } = useUIActions()
  const soundStoreActions = useSoundActions()
  const currentPlayerId = useGetCurrentPlayerId()
  const { lobbyDisabled, externalLobbyUrl } = useGetGameConfig()

  const handleBackToHome = () => {
    if (externalLobbyUrl && typeof window !== 'undefined' && window.top) {
      // Redirect broswer to external lobby URL if in an iframe
      if (window.self !== window.top) {
        window.top.location.href = externalLobbyUrl
      } else {
        window.location.href = externalLobbyUrl
      }

      datadogLogs.logger.info('tracks_panel', {
        user_action: {
          type: 'go_to_external_lobby',
          lobbyDisabled,
          externalLobbyUrl,
          playerId: currentPlayerId,
          isInIframe: window.self !== window.top
        }
      })
    }
  }

  const handleGoToLobby = () => {
    router.push(lobbyPath())
    tracksPanelOpened(false)
    soundStoreActions.clearAllNotifications()

    datadogLogs.logger.info('tracks_panel', {
      user_action: {
        type: 'go_to_lobby',
        currentGame: gameId,
        playerId: currentPlayerId
      }
    })
  }

  const handleClose = () => {
    tracksPanelOpened(false)

    datadogLogs.logger.info('tracks_panel', {
      user_action: {
        type: 'close_button',
        currentGame: gameId,
        playerId: currentPlayerId
      }
    })
  }

  return (
    <div className="game-tracks-panel__button-container">
      {lobbyDisabled && externalLobbyUrl ? (
        <Button
          className="game-tracks-panel__button left-button external-button"
          onClick={handleBackToHome}
          useDefaultClasses={false}
        >
          <span>{t('Back to Home')}</span>
          <span className="link-icon">
            <ExternalLinkIcon />
          </span>
        </Button>
      ) : null}

      {!lobbyDisabled && (
        <Button
          className="game-tracks-panel__button left-button"
          onClick={handleGoToLobby}
          useDefaultClasses={false}
        >
          <ArrowLeftIcon />
          <span>{t('Lobby')}</span>
          <One55SymbolWhiteSimple />
        </Button>
      )}

      {!isScheduled && (
        <Button
          className="game-tracks-panel__button right-button"
          onClick={handleClose}
          useDefaultClasses={false}
        >
          <TimesIcon />
          <span>{t('Close')}</span>
        </Button>
      )}
    </div>
  )
}
